import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Calendar, Link as LinkIcon, Save, Trash, RotateCcw, Loader2, User, ExternalLink, Image, FileText } from 'lucide-react';
import { Editor } from '@tinymce/tinymce-react';

const OpinionForm = ({ 
  opinion = null, 
  onSubmit, 
  onCancel,
  isLoading = false,
  submitButtonText = 'Submit Opinion',
  showDelete = false,
  onDelete = null,
}) => {
  // Form state
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [content, setContent] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [metaImageUrl, setMetaImageUrl] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [formError, setFormError] = useState(null);
  const [status, setStatus] = useState('draft');
  const [readingTime, setReadingTime] = useState(5);
  
  // AI generation state
  const [showGenerator, setShowGenerator] = useState(false);
  const [articleContent, setArticleContent] = useState('');
  const [stance, setStance] = useState('');
  const [tone, setTone] = useState('passionate');
  const [length, setLength] = useState('standard');
  const [generatingOpinion, setGeneratingOpinion] = useState(false);
  
  // Load existing opinion data if provided
  useEffect(() => {
    if (opinion) {
      setTitle(opinion.title || '');
      setAuthor(opinion.author || '');
      setContent(opinion.content || '');
      setSourceUrl(opinion.source_url || '');
      setMetaImageUrl(opinion.meta_image_url || '');
      setMetaDescription(opinion.meta_description || '');
      setStatus(opinion.status || 'draft');
      setReadingTime(opinion.reading_time_minutes || 5);
      
      // If we have source article content, set that too
      if (opinion.source_article_content) {
        setArticleContent(opinion.source_article_content);
      }
    }
  }, [opinion]);
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);
    
    // Basic validation
    if (!title.trim()) {
      setFormError('Opinion title is required');
      return;
    }
    
    if (!author.trim()) {
      setFormError('Author name is required');
      return;
    }
    
    if (!content.trim()) {
      setFormError('Opinion content is required');
      return;
    }
    
    // Prepare data for submission
    const opinionData = {
      title: title.trim(),
      author: author.trim(),
      content: content.trim(),
      source_url: sourceUrl.trim(),
      meta_image_url: metaImageUrl.trim(),
      meta_description: metaDescription.trim(),
      status,
      reading_time_minutes: readingTime,
      source_article_content: articleContent.trim() || null
    };
    
    // If editing an existing opinion, include the ID
    if (opinion && opinion.id) {
      opinionData.id = opinion.id;
    }
    
    // Call the submit handler
    onSubmit(opinionData);
  };
  
  // Handle reset form
  const handleReset = () => {
    if (opinion) {
      // Reset to original opinion data
      setTitle(opinion.title || '');
      setAuthor(opinion.author || '');
      setContent(opinion.content || '');
      setSourceUrl(opinion.source_url || '');
      setMetaImageUrl(opinion.meta_image_url || '');
      setMetaDescription(opinion.meta_description || '');
      setStatus(opinion.status || 'draft');
      setReadingTime(opinion.reading_time_minutes || 5);
    } else {
      // Clear form for new opinion
      setTitle('');
      setAuthor('');
      setContent('');
      setSourceUrl('');
      setMetaImageUrl('');
      setMetaDescription('');
      setStatus('draft');
      setReadingTime(5);
    }
    setFormError(null);
  };
  
  // Handle delete confirmation
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this opinion? This action cannot be undone.')) {
      onDelete && onDelete(opinion.id);
    }
  };
  
  // Handle opinion generation with AI
  const generateOpinion = async () => {
    if (!articleContent.trim()) {
      setFormError('Please enter the article content to analyze');
      return;
    }
    
    if (!stance.trim()) {
      setFormError('Please enter your stance/direction for the opinion piece');
      return;
    }
    
    try {
      setGeneratingOpinion(true);
      
      const response = await fetch('/api/opinions/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          article_content: articleContent,
          stance,
          tone,
          length
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to generate opinion');
      }
      
      const data = await response.json();
      
      // Update form with generated content
      setTitle(data.title || 'Generated Opinion');
      setContent(data.content);
      
      // Set meta description if provided
      if (data.meta_description) {
        setMetaDescription(data.meta_description);
      }
      
      // Set reading time if provided
      if (data.reading_time_minutes) {
        setReadingTime(data.reading_time_minutes);
      }
      
      // Keep the source article content for reference
      setArticleContent(data.source_article);
      
      // Hide the generator panel
      setShowGenerator(false);
    } catch (error) {
      setFormError('Error generating opinion: ' + error.message);
    } finally {
      setGeneratingOpinion(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error message */}
      {formError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{formError}</span>
          </div>
        </div>
      )}
      
      {/* AI Opinion Generator */}
      <div className="bg-purple-50 p-4 rounded-md border border-purple-200 mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-purple-800 font-medium">AI Opinion Generator</h3>
          <button
            type="button"
            onClick={() => setShowGenerator(!showGenerator)}
            className="text-purple-600 hover:text-purple-800 font-medium"
          >
            {showGenerator ? 'Hide Generator' : 'Show Generator'}
          </button>
        </div>
        
        {showGenerator && (
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="articleContent" className="block text-sm font-medium text-gray-700 mb-1">
                Source Article Content
              </label>
              <textarea
                id="articleContent"
                name="articleContent"
                rows={10}
                value={articleContent}
                onChange={(e) => setArticleContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-normal"
                style={{ lineHeight: "1.5", fontFamily: "sans-serif" }}
                placeholder="Paste the full article text here..."
              />
            </div>
            
            <div>
              <label htmlFor="stance" className="block text-sm font-medium text-gray-700 mb-1">
                Your Stance/Direction
              </label>
              <textarea
                id="stance"
                name="stance"
                rows={3}
                value={stance}
                onChange={(e) => setStance(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Briefly describe your position or angle on this article (2-3 sentences)..."
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
                  Tone
                </label>
                <select
                  id="tone"
                  name="tone"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="measured">Measured</option>
                  <option value="passionate">Passionate</option>
                  <option value="analytical">Analytical</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="length" className="block text-sm font-medium text-gray-700 mb-1">
                  Length
                </label>
                <select
                  id="length"
                  name="length"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="brief">Brief (500-750 words)</option>
                  <option value="standard">Standard (1000-1500 words)</option>
                  <option value="in-depth">In-depth (2000-2500 words)</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={generateOpinion}
                  disabled={generatingOpinion}
                  className="w-full inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {generatingOpinion ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                      Generating...
                    </>
                  ) : (
                    'Generate Opinion'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Basic Info Section */}
      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mb-6">
        <h3 className="text-blue-800 font-medium mb-4">Basic Opinion Information</h3>
        
        {/* Title */}
        <div className="mb-6">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Opinion Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="Enter opinion title"
            required
          />
        </div>
        
        {/* Author */}
        <div className="mb-6">
          <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              Author *
            </div>
          </label>
          <input
            type="text"
            id="author"
            name="author"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="Enter author name"
            required
          />
        </div>
        
        {/* Status & Reading Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Publication Status
            </label>
            <select
              id="status"
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="readingTime" className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Reading Time (minutes)
              </div>
            </label>
            <input
              type="number"
              id="readingTime"
              name="readingTime"
              min="1"
              max="60"
              value={readingTime}
              onChange={(e) => setReadingTime(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
      
      {/* Source Section */}
      <div className="bg-green-50 p-4 rounded-md border border-green-200 mb-6">
        <h3 className="text-green-800 font-medium mb-4">Source & Meta Information</h3>
        
        {/* Source Link */}
        <div className="mb-6">
          <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <ExternalLink className="h-4 w-4 mr-1" />
              Source Article URL
            </div>
          </label>
          <input
            type="url"
            id="sourceUrl"
            name="sourceUrl"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="https://example.com/article"
          />
          <p className="mt-1 text-sm text-gray-500">
            Link to the source article (if applicable)
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Meta Image URL */}
          <div>
            <label htmlFor="metaImageUrl" className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center">
                <Image className="h-4 w-4 mr-1" />
                Featured Image URL
              </div>
            </label>
            <input
              type="url"
              id="metaImageUrl"
              name="metaImageUrl"
              value={metaImageUrl}
              onChange={(e) => setMetaImageUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="https://example.com/image.jpg"
            />
            <p className="mt-1 text-sm text-gray-500">
              URL for the featured image (for social sharing)
            </p>
          </div>
          
          {/* Meta Description */}
          <div>
            <label htmlFor="metaDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Meta Description
            </label>
            <textarea
              id="metaDescription"
              name="metaDescription"
              rows={3}
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Brief description for search engines and social sharing..."
            />
            <p className="mt-1 text-sm text-gray-500">
              Used for SEO and social media sharing
            </p>
          </div>
        </div>
      </div>
      
      {/* Content Section */}
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-6">
        <h3 className="text-gray-800 font-medium mb-4">
          <div className="flex items-center">
            <FileText className="h-4 w-4 mr-1" />
            Opinion Content
          </div>
        </h3>
        
        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
            Main Content *
          </label>
          <Editor
            apiKey="7gb9evx0yowkkg5jtqe24z1r00texiiitgid5jcph71w4wep"
            value={content}
            onEditorChange={(newContent) => {
              // Clean up the content to ensure consistent paragraph spacing
              const cleanedContent = newContent
                .replace(/\n+/g, ' ')
                .replace(/<p>\s*<\/p>/g, '');
              setContent(cleanedContent);
            }}
            init={{
              height: 500,
              menubar: true,
              plugins: [
                // Only using free Community edition features
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
              ],
              toolbar: 'undo redo | blocks | formatselect | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link image media table | removeformat help',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:16px } p { margin-bottom: 0.5em; margin-top: 0; line-height: 1.5; } p + p { margin-top: 0.85em; }',
              placeholder: "Enter the opinion content here...",
              branding: false,
              promotion: false
            }}
          />
          <p className="mt-1 text-sm text-gray-500">
            Full content of the opinion piece. Use the toolbar to format your text.
          </p>
        </div>
      </div>
      
      {/* Form actions */}
      <div className="flex justify-between pt-5">
        <div className="flex">
          {/* Cancel button */}
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          
          {/* Reset button */}
          <button
            type="button"
            onClick={handleReset}
            className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </button>
          
          {/* Delete button (if editing) */}
          {showDelete && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="ml-3 inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Trash className="h-4 w-4 mr-1" />
              Delete
            </button>
          )}
        </div>
        
        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 ${
            isLoading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
              Processing...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-1" />
              {submitButtonText}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default OpinionForm;